import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/i18n/index.jsx';
import { getAdsConsent, CONSENT_EVENT } from '@/utils/consent.js';

function AdSense({ 
  adSlot = "2244789490", 
  adFormat = 'auto', 
  className = '', 
  style = {} 
}) {
  const adRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { lang } = useI18n();
  const isEN = lang === 'en';

  // Lazy loading with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Load slightly before it comes into view
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Push ad when visible AND advertising consent has been granted.
  // Re-evaluates when consent changes (e.g. visitor accepts after the slot
  // scrolled into view) so an ad can render once the library is available.
  useEffect(() => {
    const tryPush = () => {
      if (!isVisible) return;
      if (getAdsConsent() !== 'granted') return; // no consent -> no ad
      if (!adRef.current || adRef.current.hasAttribute('data-adsbygoogle-status')) return;
      try {
        if (window && window.adsbygoogle) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (error) {
        console.error('Google AdSense error:', error);
      }
    };
    tryPush();
    window.addEventListener(CONSENT_EVENT, tryPush);
    return () => window.removeEventListener(CONSENT_EVENT, tryPush);
  }, [isVisible]);

  // Determine dimensions and classes based on format to reserve space and prevent layout shifts
  let containerClasses = 'format-auto';
  let containerMinHeight = 90;
  
  if (adFormat === 'vertical') {
    containerClasses = 'format-vertical w-[300px] mx-auto';
    containerMinHeight = 600;
  } else if (adFormat === 'rectangle') {
    containerClasses = 'format-rectangle w-[300px] mx-auto';
    containerMinHeight = 250;
  } else if (adFormat === 'horizontal') {
    containerClasses = 'format-horizontal max-w-[970px] mx-auto';
    containerMinHeight = 90;
  }

  return (
    <div className={`ad-wrapper ${className}`} style={style}>
      <span className="text-[10px] uppercase text-muted-foreground/60 mb-2 tracking-wider font-medium select-none">
        {isEN ? 'Advertisement' : 'Publicidade'}
      </span>
      <div className={`ad-container ${containerClasses}`} style={{ minHeight: containerMinHeight }}>
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', width: '100%', height: '100%' }}
          data-ad-client="ca-pub-4682982658299645"
          data-ad-slot={adSlot}
          data-ad-format={adFormat}
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
}

export default AdSense;